import { logEvent, setUserId, setUserProperties } from 'firebase/analytics';
import { analytics } from '@/lib/firebase';

/**
 * Analytics Service
 * Handles Google Analytics event tracking for the application
 */
export const analyticsService = {
  /**
   * Log page view - automatically called on route changes
   * @param path - The page path (e.g., '/billing/dashboard')
   * @param domain - The current domain (e.g., 'billing', 'workflow')
   * @param title - The page title (defaults to document.title)
   */
  logPageView: (path: string, domain?: string, title?: string) => {
    if (!analytics) {
      // Analytics not initialized (dev mode or not supported)
      return;
    }
    
    try {
      logEvent(analytics, 'page_view', {
        page_path: path,
        page_title: title || document.title,
        page_location: window.location.href,
        domain: domain || 'unknown'
      });
      
      console.log('📊 [Analytics] Page view tracked:', { path, domain, title });
    } catch (error) {
      console.error('❌ [Analytics] Failed to log page view:', error);
    }
  },

  /**
   * Set user ID for analytics tracking
   * @param userId - The user's unique identifier
   */
  setUserId: (userId: string) => {
    if (!analytics) return;
    
    try {
      setUserId(analytics, userId);
      console.log('📊 [Analytics] User ID set:', userId);
    } catch (error) {
      console.error('❌ [Analytics] Failed to set user ID:', error);
    }
  },

  /**
   * Set user properties for segmentation
   * @param properties - Object containing user properties
   */
  setUserProperties: (properties: {
    user_email?: string;
    user_role?: string;
    current_program?: string;
  }) => {
    if (!analytics) return;
    
    try {
      setUserProperties(analytics, properties);
      console.log('📊 [Analytics] User properties set:', properties);
    } catch (error) {
      console.error('❌ [Analytics] Failed to set user properties:', error);
    }
  },

  /**
   * Track when a user adds a new workflow spot
   * @param teamId - The team ID where the spot was added
   * @param teamName - The team name
   * @param userId - The user who created the spot
   */
  logWorkflowSpotAdded: (teamId: string, teamName: string, userId: string) => {
    if (!analytics) return;
    
    try {
      logEvent(analytics, 'workflow_spot_added', {
        team_id: teamId,
        team_name: teamName,
        user_id: userId,
        timestamp: new Date().toISOString()
      });
      console.log('📊 [Analytics] Workflow spot added tracked');
    } catch (error) {
      console.error('❌ [Analytics] Failed to log workflow spot added:', error);
    }
  },

  /**
   * Track when a user adds a new hiring spot
   * @param teamId - The team ID where the spot was added
   * @param teamName - The team name
   * @param role - The role for the hiring spot
   * @param userId - The user who created the spot
   */
  logHiringSpotAdded: (teamId: string, teamName: string, role: string, userId: string) => {
    if (!analytics) return;
    
    try {
      logEvent(analytics, 'hiring_spot_added', {
        team_id: teamId,
        team_name: teamName,
        role: role,
        user_id: userId,
        timestamp: new Date().toISOString()
      });
      console.log('📊 [Analytics] Hiring spot added tracked');
    } catch (error) {
      console.error('❌ [Analytics] Failed to log hiring spot added:', error);
    }
  },

  /**
   * Track when a user is assigned to a workflow
   * @param workflowId - The workflow ID
   * @param workflowName - The workflow name
   * @param assignedUserId - The user being assigned
   * @param assignedByUserId - The user making the assignment
   */
  logUserAssignedToWorkflow: (
    workflowId: string,
    workflowName: string,
    assignedUserId: string,
    assignedByUserId: string
  ) => {
    if (!analytics) return;
    
    try {
      logEvent(analytics, 'user_assigned_to_workflow', {
        workflow_id: workflowId,
        workflow_name: workflowName,
        assigned_user_id: assignedUserId,
        assigned_by_user_id: assignedByUserId,
        timestamp: new Date().toISOString()
      });
      console.log('📊 [Analytics] User assignment tracked');
    } catch (error) {
      console.error('❌ [Analytics] Failed to log user assignment:', error);
    }
  },

  /**
   * Track when a hiring spot is approved
   * @param spotId - The hiring spot ID
   * @param role - The role for the hiring spot
   * @param userId - The user who approved it
   */
  logHiringSpotApproved: (spotId: string, role: string, userId: string) => {
    if (!analytics) return;
    
    try {
      logEvent(analytics, 'hiring_spot_approved', {
        spot_id: spotId,
        role: role,
        user_id: userId,
        timestamp: new Date().toISOString()
      });
      console.log('📊 [Analytics] Hiring spot approval tracked');
    } catch (error) {
      console.error('❌ [Analytics] Failed to log hiring spot approval:', error);
    }
  },

  /**
   * Track when a hiring spot is marked as filled
   * @param spotId - The hiring spot ID
   * @param role - The role for the hiring spot
   * @param userId - The user who marked it as filled
   */
  logHiringSpotFilled: (spotId: string, role: string, userId: string) => {
    if (!analytics) return;
    
    try {
      logEvent(analytics, 'hiring_spot_filled', {
        spot_id: spotId,
        role: role,
        user_id: userId,
        timestamp: new Date().toISOString()
      });
      console.log('📊 [Analytics] Hiring spot filled tracked');
    } catch (error) {
      console.error('❌ [Analytics] Failed to log hiring spot filled:', error);
    }
  },

  /**
   * Track when a hiring spot is archived
   * @param spotId - The hiring spot ID
   * @param role - The role for the hiring spot
   * @param userId - The user who archived it
   */
  logHiringSpotArchived: (spotId: string, role: string, userId: string) => {
    if (!analytics) return;
    
    try {
      logEvent(analytics, 'hiring_spot_archived', {
        spot_id: spotId,
        role: role,
        user_id: userId,
        timestamp: new Date().toISOString()
      });
      console.log('📊 [Analytics] Hiring spot archived tracked');
    } catch (error) {
      console.error('❌ [Analytics] Failed to log hiring spot archived:', error);
    }
  },

  /**
   * Track when a workflow spot is approved
   * @param spotId - The workflow spot ID
   * @param userId - The user who approved it
   */
  logWorkflowSpotApproved: (spotId: string, userId: string) => {
    if (!analytics) return;
    
    try {
      logEvent(analytics, 'workflow_spot_approved', {
        spot_id: spotId,
        user_id: userId,
        timestamp: new Date().toISOString()
      });
      console.log('📊 [Analytics] Workflow spot approval tracked');
    } catch (error) {
      console.error('❌ [Analytics] Failed to log workflow spot approval:', error);
    }
  },

  /**
   * Track when a workflow spot is archived
   * @param spotId - The workflow spot ID
   * @param userId - The user who archived it
   */
  logWorkflowSpotArchived: (spotId: string, userId: string) => {
    if (!analytics) return;
    
    try {
      logEvent(analytics, 'workflow_spot_archived', {
        spot_id: spotId,
        user_id: userId,
        timestamp: new Date().toISOString()
      });
      console.log('📊 [Analytics] Workflow spot archived tracked');
    } catch (error) {
      console.error('❌ [Analytics] Failed to log workflow spot archived:', error);
    }
  },

  /**
   * Track when a confirmed hiring placement is added
   * @param hireName - The name of the hire
   * @param role - The role
   * @param department - The department
   * @param userId - The user who added it
   */
  logConfirmedHireAdded: (
    hireName: string,
    role: string,
    department: string,
    userId: string
  ) => {
    if (!analytics) return;
    
    try {
      logEvent(analytics, 'confirmed_hire_added', {
        hire_name: hireName,
        role: role,
        department: department,
        user_id: userId,
        timestamp: new Date().toISOString()
      });
      console.log('📊 [Analytics] Confirmed hire tracked');
    } catch (error) {
      console.error('❌ [Analytics] Failed to log confirmed hire:', error);
    }
  },

  /**
   * Track when the hiring forecast is updated
   * @param department - The department
   * @param roleGrade - The role grade
   * @param userId - The user who updated it
   */
  logForecastUpdated: (department: string, roleGrade: string, userId: string) => {
    if (!analytics) return;
    
    try {
      logEvent(analytics, 'hiring_forecast_updated', {
        department: department,
        role_grade: roleGrade,
        user_id: userId,
        timestamp: new Date().toISOString()
      });
      console.log('📊 [Analytics] Forecast update tracked');
    } catch (error) {
      console.error('❌ [Analytics] Failed to log forecast update:', error);
    }
  },

  /**
   * Track when Critical Success Factors are updated
   * @param cardId - The card ID
   * @param cardType - The type of card (company/department)
   * @param csfId - The CSF ID
   * @param newStatus - The new status color
   * @param userId - The user who updated it
   */
  logCSFUpdated: (
    cardId: string,
    cardType: string,
    csfId: string,
    newStatus: string,
    userId: string
  ) => {
    if (!analytics) return;
    
    try {
      logEvent(analytics, 'csf_updated', {
        card_id: cardId,
        card_type: cardType,
        csf_id: csfId,
        new_status: newStatus,
        user_id: userId,
        timestamp: new Date().toISOString()
      });
      console.log('📊 [Analytics] CSF update tracked');
    } catch (error) {
      console.error('❌ [Analytics] Failed to log CSF update:', error);
    }
  },
};